import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import { normalizeErrorCode, RecoverPinError } from '../models';
import type {
  GeneratePinRecoveryOtpResponse,
  PinRecoveryResponse,
  RecoverPinErrorBody,
  RecoverPinOtpChannel,
  ValidatePasswordResponse,
} from '../models';

/** Default message shown when the backend gives no usable error text. */
const FALLBACK_ERROR = 'Une erreur est survenue. Veuillez réessayer.';

/** Non-null shape of the stored session data returned by `Storage.getUserData()`. */
type StoredUserData = NonNullable<ReturnType<Storage['getUserData']>>;

/** Common credentials sent with every recover-pin request. */
interface RecoverPinBasePayload {
  merchant_id: string;
  user_type: 'merchant';
  device_type: string;
  account_id: string;
  merchant_user_id: string;
}

/**
 * RecoverPinApi — HTTP calls for the "Recover security code" (PIN reset) flow.
 *
 * Three endpoints, in flow order:
 *  1. POST /api/gozem_money_auth/validate_password         → confirm password, list OTP channels
 *  2. POST /api/gozem_money_auth/generate_pin_recovery_otp → send the OTP via the chosen channel
 *  3. POST /api/gozem_money_auth/pin_recovery              → submit OTP + new (encrypted) PIN
 *
 * The password is carried through all three calls — the backend issues no
 * intermediate token. There is no standalone "verify OTP" endpoint: the OTP is
 * validated inside `pin_recovery` together with the new PIN.
 *
 * Auth credentials (merchant_id, account_id, merchant_user_id) and the user's
 * phone / country phone code are read from localStorage via `Storage`.
 * Every method throws a {@link RecoverPinError} (carrying `error_code` and
 * lockout metadata) for the store to map to UI state.
 */
@Injectable({
  providedIn: 'root',
})
export class RecoverPinApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);

  /** Step 1 — verify the account password and retrieve the available OTP channels. */
  validatePassword(password: string): Observable<ValidatePasswordResponse> {
    const userData = this.requireUserData();
    const payload = {
      ...this.basePayload(userData),
      phone: this.localPhone(userData),
      country_phone_code: userData.countryDetail.countryphonecode ?? '',
      password,
    };
    return this.http
      .post<ValidatePasswordResponse | RecoverPinErrorBody>(
        `${environment.apiUrl}/api/gozem_money_auth/validate_password`,
        payload,
      )
      .pipe(
        map((res) => {
          if (!res.success || !res.is_password_valid) {
            throw this.buildError(res as RecoverPinErrorBody);
          }
          return res;
        }),
        catchError((error: unknown) => this.handleError(error)),
      );
  }

  /** Step 2 — request a verification code via the chosen channel. */
  generateOtp(
    password: string,
    channel: RecoverPinOtpChannel,
  ): Observable<GeneratePinRecoveryOtpResponse> {
    const payload = {
      ...this.basePayload(this.requireUserData()),
      password,
      otp_channel: channel,
    };
    return this.http
      .post<GeneratePinRecoveryOtpResponse | RecoverPinErrorBody>(
        `${environment.apiUrl}/api/gozem_money_auth/generate_pin_recovery_otp`,
        payload,
      )
      .pipe(
        map((res) => {
          if (!res.success) {
            throw this.buildError(res as RecoverPinErrorBody);
          }
          return res;
        }),
        catchError((error: unknown) => this.handleError(error)),
      );
  }

  /** Step 3 — submit the OTP and the new (RSA-encrypted) PIN to reset it. */
  recoverPin(password: string, otp: string, encryptedPin: string): Observable<PinRecoveryResponse> {
    const payload = {
      ...this.basePayload(this.requireUserData()),
      password,
      otp,
      new_pin_code: encryptedPin,
    };
    return this.http
      .post<PinRecoveryResponse | RecoverPinErrorBody>(
        `${environment.apiUrl}/api/gozem_money_auth/pin_recovery`,
        payload,
      )
      .pipe(
        map((res) => {
          if (!res.success) {
            throw this.buildError(res as RecoverPinErrorBody);
          }
          return res;
        }),
        catchError((error: unknown) => this.handleError(error)),
      );
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Stored session data, or a typed error if the user is no longer logged in. */
  private requireUserData(): StoredUserData {
    const userData = this.storage.getUserData();
    if (!userData) {
      throw new RecoverPinError(null, 'Session introuvable. Veuillez vous reconnecter.');
    }
    return userData;
  }

  private basePayload(userData: StoredUserData): RecoverPinBasePayload {
    return {
      merchant_id: userData.merchantId,
      user_type: 'merchant',
      device_type: environment.deviceType,
      // Active account, not accounts[0]: pinning to the first row ignores which
      // merchant the user actually selected. See AccountStore.resolveAccountId.
      account_id: this.accountStore.resolveAccountId(),
      merchant_user_id: userData.merchantUserId,
    };
  }

  /**
   * Local phone number, with the country phone code prefix stripped.
   * e.g. country "+228" + stored "+22896384846" → "96384846".
   */
  private localPhone(userData: StoredUserData): string {
    const code = userData.countryDetail.countryphonecode ?? '';
    let phone = (userData.user.phone ?? '').trim();
    if (code && phone.startsWith(code)) {
      phone = phone.slice(code.length);
    }
    return phone.replaceAll(/\D/g, '');
  }

  /** Build a typed error from a `success: false` response body. */
  private buildError(body: RecoverPinErrorBody | null | undefined): RecoverPinError {
    return new RecoverPinError(
      normalizeErrorCode(body?.error_code),
      body?.message ?? FALLBACK_ERROR,
      body?.metadata?.remaining_attempts ?? null,
      body?.metadata?.block_time ?? null,
    );
  }

  /** Normalize anything thrown along the pipe into a {@link RecoverPinError}. */
  private handleError(error: unknown): Observable<never> {
    if (error instanceof RecoverPinError) {
      return throwError(() => error);
    }
    if (error instanceof HttpErrorResponse) {
      return throwError(() => this.buildError(error.error as RecoverPinErrorBody | null));
    }
    return throwError(() => new RecoverPinError(null, FALLBACK_ERROR));
  }
}
